import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type { Driver, DriverGroup } from "../types";
import { fullDriversFromResponse, type DriverListResponse } from "../driverResponses";
import { useToast } from "../toast";
import ConfirmDialog from "./ConfirmDialog";

interface CreateGroupDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

function CreateGroupDialog({ open, onClose, onSaved }: CreateGroupDialogProps) {
  const api = useAuthApi();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api(`${MGMT}/driver-groups`, {
        method: "POST",
        body: JSON.stringify({ name: name.trim(), description: description.trim() || undefined }),
      });
      toast.show("Group created");
      setName("");
      setDescription("");
      onSaved();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New Driver Group</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth />
          <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !name.trim()}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface AddMemberDialogProps {
  open: boolean;
  group: DriverGroup | null;
  drivers: Driver[];
  onClose: () => void;
  onSaved: () => void;
}

function AddMemberDialog({ open, group, drivers, onClose, onSaved }: AddMemberDialogProps) {
  const api = useAuthApi();
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState<number | null>(null);
  const toast = useToast();

  const filtered = drivers.filter(
    (d) =>
      d.name.toLowerCase().includes(search.toLowerCase()) ||
      d.email.toLowerCase().includes(search.toLowerCase())
  );

  const add = async (driver: Driver) => {
    if (!group) return;
    setSaving(driver.id);
    try {
      await api(`${MGMT}/drivers/${driver.id}/groups/${group.id}`, { method: "POST" });
      toast.show(`${driver.name} added to ${group.name}`);
      onSaved();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Drivers to "{group?.name}"</DialogTitle>
      <DialogContent>
        <TextField
          label="Search drivers"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          sx={{ mb: 2, mt: 1 }}
        />
        <TableContainer>
          <Table size="small">
            <TableBody>
              {filtered.map((d) => (
                <TableRow key={d.id}>
                  <TableCell>{d.name}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{d.email}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      onClick={() => add(d)}
                      disabled={saving === d.id}
                    >
                      Add
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function DriverGroupsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const api = useAuthApi();
  const [groups, setGroups] = useState<DriverGroup[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<DriverGroup | null>(null);
  const [addingMembersTo, setAddingMembersTo] = useState<DriverGroup | null>(null);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, d] = await Promise.all([
        api<DriverGroup[]>(`${MGMT}/driver-groups`),
        api<DriverListResponse>(`${MGMT}/drivers`),
      ]);
      setGroups(g ?? []);
      setDrivers(fullDriversFromResponse(d));
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api(`${MGMT}/driver-groups/${deleting.id}`, { method: "DELETE" });
      toast.show("Group deleted");
      setDeleting(null);
      load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <span>Driver Groups</span>
            <Stack direction="row" spacing={1}>
              <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading} size="small">
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreating(true)}
                size="small"
              >
                New Group
              </Button>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!loading && groups.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No driver groups yet.
            </Typography>
          )}
          {!loading && groups.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.map((g) => (
                    <TableRow key={g.id}>
                      <TableCell sx={{ fontWeight: 600 }}>{g.name}</TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>{g.description ?? "–"}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          title="Add members"
                          onClick={() => setAddingMembersTo(g)}
                        >
                          <GroupAddIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => setDeleting(g)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <CreateGroupDialog
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={() => {
          setCreating(false);
          load();
        }}
      />

      <AddMemberDialog
        open={addingMembersTo !== null}
        group={addingMembersTo}
        drivers={drivers}
        onClose={() => setAddingMembersTo(null)}
        onSaved={() => {}}
      />

      <ConfirmDialog
        open={deleting !== null}
        title="Delete group?"
        message={`Delete group "${deleting?.name}"? Members will not be deleted.`}
        onCancel={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </>
  );
}
